import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IncomingCall as IncomingCallData } from '../api.ts';
import { preferenceEnabled } from '../preferences.ts';

/**
 * Chiamata in arrivo.
 *
 * Venticinque secondi, poi passa al volontario successivo (sezione 5.1). Il
 * conto alla rovescia serve a far capire che l'attesa non è infinita, non a
 * mettere fretta: chi non può rispondere ha un pulsante per dirlo subito.
 *
 * L'annuncio è `assertive` **oltre** al suono: chi usa NVDA o VoiceOver con
 * l'audio del sistema occupato deve comunque saperlo (sezione 7bis). Il
 * conto alla rovescia invece resta fuori da ogni regione live, altrimenti
 * verrebbe riletto a ogni secondo.
 */

type Props = {
  call: IncomingCallData;
  /** Il titolo riceve il focus quando questa schermata subentra. */
  headingRef?: React.RefObject<HTMLElement | null>;
  onAnswer: () => void;
  onDecline: () => void;
};

export function IncomingCall({ call, headingRef, onAnswer, onDecline }: Props) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(() => secondsUntil(call.expiresAt));

  useEffect(() => {
    setRemaining(secondsUntil(call.expiresAt));
    const handle = setInterval(() => setRemaining(secondsUntil(call.expiresAt)), 1000);
    return () => clearInterval(handle);
  }, [call.expiresAt]);

  return (
    <section className="ringing" aria-labelledby="ringing-heading">
      {/*
        L'annuncio urgente lo fa la regione persistente della console: una
        regione live che compare già piena non viene letta.
      */}
      <p className="ringing__eyebrow">{t('incoming.title')}</p>
      {/* `h3`: il titolo di pagina è quello della console, e questo è un riquadro. */}
      <h3
        id="ringing-heading"
        ref={headingRef as React.RefObject<HTMLHeadingElement> | undefined}
        tabIndex={-1}
      >
        {t(call.anonymous ? 'incoming.anonymous' : 'incoming.registered')}
      </h3>
      {/*
        Quale canale, e prima di rispondere.
        Non è una rifinitura: chi sta per rispondere a un video deve saperlo
        **prima** di premere, perché fra un attimo sarà davanti a una camera —
        e perché una chat si può prendere anche stando in piedi in cucina,
        mentre una videochiamata no.
      */}
      {/*
        Non `.muted`: è la riga che si legge **prima** di premere, e portava lo
        stesso grigio piccolo delle due righe di coda. Il canale e i secondi
        che restano decidono se si risponde adesso; "se non rispondi passa a un
        altro" e la scorciatoia si leggono dopo, se si legge.
      */}
      <p className="ringing__meta">
        {t(channelKey(call.channel))} · {t('incoming.countdown', { seconds: remaining })}
      </p>

      {/*
        A tutta larghezza come "non posso", non piu' stretto di lui: e' il
        pulsante che si preme al buio, con il telefono in una mano. Restava
        alla larghezza del suo testo mentre quello sotto era largo quanto lo
        schermo, cioe' il bersaglio piu' piccolo era quello da prendere.
      */}
      <button
        type="button"
        className="button button--primary button--big button--wide"
        onClick={onAnswer}
      >
        {t('incoming.answer')}
      </button>
      <button type="button" className="button button--wide" onClick={onDecline}>
        {t('incoming.decline')}
      </button>
      <p className="muted">{t('incoming.next')}</p>
      {/*
        La scorciatoia ricordata dove serve, e **solo se è accesa**: chi l'ha
        disattivata (X12) continuava a leggere una promessa che non vale.
      */}
      {preferenceEnabled('console.shortcuts') ? (
        <p className="muted">{t('incoming.keyboard_hint')}</p>
      ) : null}
    </section>
  );
}

function channelKey(channel: string): string {
  if (channel === 'video') return 'incoming.channel_video';
  if (channel === 'chat') return 'incoming.channel_chat';
  return 'incoming.channel_audio';
}

function secondsUntil(iso: string): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000));
}
